"use client"

import { useState } from "react"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Label } from "@/components/ui/label"
import { Body } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { useHighlight } from "@chakra-ui/react"

type TermsSectionProps = {
	title: string
	content: string
	links?: {
		text: string
		url: string
	}[]
}

const TermsSection = ({ title, content, links }: TermsSectionProps) => {
	const chunks = useHighlight({
		text: content,
		query: links?.map((x) => x.text) || [],
	})
	return (
		<div className="space-y-1">
			<Body className="font-bold underline underline-offset-2">{title}</Body>
			<Body>
				{chunks.map(({ text, match }, index) => {
					if (!match) return text
					return (
						<a
							key={index}
							href={links?.find((x) => x.text === text)?.url || "#"}
							target="_blank"
							rel="noreferrer"
							className="text-primary"
						>
							{text}
						</a>
					)
				})}
			</Body>
		</div>
	)
}

type TermsDialogContentProps = {
	onAccept: () => void
}
export const TermsDialogContent = (props: TermsDialogContentProps) => {
	const [agreed, setAgreed] = useState(false)

	return (
		<DialogContent closeVisible className="max-w-2xl">
			<DialogHeader>
				<DialogTitle className="text-lg">
					To proceed, please accept our Terms of Service
				</DialogTitle>
			</DialogHeader>
			<div className="space-y-6">
				<ScrollArea className="h-[400px] rounded-lg border p-4">
					<div className="space-y-4">
						<div>
							<Body className="font-bold underline underline-offset-2">
								DISCLAIMER:
							</Body>
							<Body className="font-bold underline underline-offset-2">
								MILEKEEPER AI REPORT GENERATOR
							</Body>
						</div>

						<p className="text-sm">
							The MileKeeper AI Report Generator (the "Service") is provided
							by Tax Technologies Group Inc. ("we," "us," or "our") and is
							intended solely for informational and educational purposes. By
							using this Service, you acknowledge and agree to the
							following:
						</p>

						<TermsSection
							title="1. Not Legal or Tax Advice."
							content="The reports generated by the MileKeeper AI Report Generator are based solely on the data you provide and are intended to assist with estimating potential mileage-based tax deductions. These reports do not constitute legal, tax, or financial advice."
						/>
						<TermsSection
							title="2. User Responsibility for Data Accuracy."
							content="The accuracy and completeness of the reports depend entirely on the accuracy of the data entered by the user. It is your responsibility to ensure that all information provided is truthful, accurate, and complete. We are not liable for any errors, omissions, or discrepancies resulting from incorrect or incomplete data entry."
						/>
						<TermsSection
							title="3. IRS and Tax Compliance."
							content="While the Service is designed to assist with the reconstruction of business mileage, compliance with all applicable IRS regulations or tax authority requirements remains the sole responsibility of the user. The Service does not guarantee acceptance of any mileage report by the IRS or other tax authorities. Users should maintain proper records and consult with tax professionals to validate their deductions."
						/>
						<TermsSection
							title="4. No Guarantee of Results."
							content="The average tax deduction mentioned in our promotional materials is based on aggregated data from past users and does not guarantee specific results for individual users. Actual deductions may vary depending on the nature of your business, applicable tax laws, and the information you provide."
						/>
						<TermsSection
							title="5. Limitation of Liability."
							content="To the maximum extent permitted by law, Tax Technologies Group Inc. shall not be held liable for any indirect, incidental, special, or consequential damages, including but not limited to lost profits, arising out of or related to the use of the Service. This includes any liability for penalties, interest, or audits imposed by the IRS or other tax authorities due to reliance on the reports generated by the Service."
						/>
						<TermsSection
							title="6. Intellectual Property."
							content="All content, materials, and software associated with the MileKeeper AI Report Generator are proprietary and owned exclusively Tax Technologies Group Inc. Unauthorized reproduction, distribution, or use of our intellectual property is strictly prohibited."
						/>
						<TermsSection
							title="7. Service Availability and Accuracy."
							content="The Service relies on AI algorithms and third-party data sources to generate reports. While we strive to provide accurate and reliable results, we do not guarantee that the Service will be error-free, uninterrupted, or meet your specific requirements."
						/>
						<TermsSection
							title="8. Data Security."
							content="We take data privacy and security seriously. Your data is encrypted and securely stored; however, no system is entirely immune to breaches. By using the Service, you acknowledge and accept the inherent risks of transmitting data over the internet."
						/>
						<TermsSection
							title="9. Acceptance of Terms."
							content="By using the MileKeeper AI Report Generator, you agree to this Disclaimer, our Terms of Use, and our Privacy Policy. If you do not agree to these terms, you must not use the Service."
							links={[
								{
									text: "Terms of Use",
									url: "https://milekeeper.ai/terms-of-use",
								},
								{
									text: "Privacy Policy",
									url: "https://milekeeper.ai/privacy-policy",
								},
							]}
						/>
						<TermsSection
							title="10. Changes to the Service."
							content="We reserve the right to modify, suspend, or discontinue the Service at any time without prior notice. Users are responsible for regularly reviewing this Disclaimer for updates."
						/>
					</div>
				</ScrollArea>

				<div className="flex items-center space-x-2">
					<Checkbox
						id="terms"
						checked={agreed}
						onCheckedChange={(checked) => setAgreed(checked as boolean)}
					/>
					<Label
						htmlFor="terms"
						className="font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
					>
						I agree to the Terms of Service
					</Label>
				</div>

				<div className="flex justify-end">
					<Button
						onClick={props.onAccept}
						disabled={!agreed}
						className={cn(
							"text-primary-foreground disabled:bg-slate-200 disabled:text-muted-foreground "
						)}
					>
						Continue
					</Button>
				</div>
			</div>
		</DialogContent>
	)
}
export const TermsDialogScreen = () => {
	const [open, setOpen] = useState(true)

	const handleContinue = () => {
		setOpen(false)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<TermsDialogContent onAccept={handleContinue} />
		</Dialog>
	)
}
